import { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash2, Plus, AlertCircle } from 'lucide-react';
import { rpc, uuid } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { motion, AnimatePresence } from 'framer-motion';

interface EngineProfile {
  id: string;
  brand: string;
  type: string;
}

interface TyreProfile {
  id: string;
  brand: string;
  type: string;
  compound: string;
}

type SubTab = 'engines' | 'tyres';



export function ProfilesTab() {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<SubTab>('engines');

  const [engines, setEngines] = useState<EngineProfile[]>([]);
  const [tyres, setTyres] = useState<TyreProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editEngine, setEditEngine] = useState<EngineProfile | null>(null);
  const [isEditingEngine, setIsEditingEngine] = useState(false);

  const [editTyre, setEditTyre] = useState<TyreProfile | null>(null);
  const [isEditingTyre, setIsEditingTyre] = useState(false);

  const [deleteEngineTarget, setDeleteEngineTarget] = useState<EngineProfile | null>(null);
  const [deleteTyreTarget, setDeleteTyreTarget] = useState<TyreProfile | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'engines') {
        const data = await rpc<EngineProfile[]>('ListEngineProfiles', {});
        setEngines(data || []);
      } else {
        const data = await rpc<TyreProfile[]>('ListTyreProfiles', {});
        setTyres(data || []);
      }
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteEngine = async () => {
    if (!deleteEngineTarget) return;
    setDeleting(true);
    try {
      await rpc('DeleteEngineProfile', { id: deleteEngineTarget.id });
      showToast(t('crud.deleted', { entity: t('entity.engineProfile') }));
      setDeleteEngineTarget(null);
      loadData();
    } catch (e: any) {
      if (e.status === 409) {
        showToast(t('profiles.deleteBlocked'), 'error');
      } else {
        showToast(e.message, 'error');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteTyre = async () => {
    if (!deleteTyreTarget) return;
    setDeleting(true);
    try {
      await rpc('DeleteTyreProfile', { id: deleteTyreTarget.id });
      showToast(t('crud.deleted', { entity: t('entity.tyreProfile') }));
      setDeleteTyreTarget(null);
      loadData();
    } catch (e: any) {
      if (e.status === 409) {
        showToast(t('profiles.deleteBlocked'), 'error');
      } else {
        showToast(e.message, 'error');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveEngine = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const brand = formData.get('brand') as string;
    const type = formData.get('type') as string;

    if (!brand.trim() || !type.trim()) {
      showToast(t('crud.requiredField', { field: 'Brand/Type' }), 'error');
      return;
    }

    try {
      if (editEngine?.id) {
        await rpc('UpdateEngineProfile', { id: editEngine.id, brand: brand.trim(), type: type.trim() });
        showToast(t('crud.updated', { entity: t('entity.engineProfile') }));
      } else {
        await rpc('CreateEngineProfile', { id: uuid(), brand: brand.trim(), type: type.trim() });
        showToast(t('crud.created', { entity: t('entity.engineProfile') }));
      }
      setIsEditingEngine(false);
      loadData();
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  const handleSaveTyre = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const brand = formData.get('brand') as string;
    const type = formData.get('type') as string;
    const compound = formData.get('compound') as string;

    if (!brand.trim() || !type.trim() || !compound) {
      showToast(t('crud.requiredField', { field: 'Brand/Type/Compound' }), 'error');
      return;
    }

    try {
      if (editTyre?.id) {
        await rpc('UpdateTyreProfile', { id: editTyre.id, brand: brand.trim(), type: type.trim(), compound });
        showToast(t('crud.updated', { entity: t('entity.tyreProfile') }));
      } else {
        await rpc('CreateTyreProfile', { id: uuid(), brand: brand.trim(), type: type.trim(), compound });
        showToast(t('crud.created', { entity: t('entity.tyreProfile') }));
      }
      setIsEditingTyre(false);
      loadData();
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-[1.4rem] font-bold text-text-primary tracking-tight">{t('tab.profiles')}</h2>
        <button
          onClick={() => {
            if (activeTab === 'engines') {
              setEditEngine(null);
              setIsEditingEngine(true);
            } else {
              setEditTyre(null);
              setIsEditingTyre(true);
            }
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer"
        >
          <Plus size={14} />
          <span className="hidden sm:inline">
            {activeTab === 'engines' ? t('profiles.addEngineProfile') : t('profiles.addTyreProfile')}
          </span>
        </button>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-border-subtle pb-4">
        {(['engines', 'tyres'] as SubTab[]).map(tb => (
          <button
            key={tb}
            onClick={() => setActiveTab(tb)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              activeTab === tb ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
            }`}
          >
            {tb === 'engines' ? t('profiles.engineProfiles') : t('profiles.tyreProfiles')}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card overflow-hidden">
          <LoadingSpinner message={t('common.loading')} />
        </div>
      ) : error ? (
        <ErrorState message={error} />
      ) : activeTab === 'engines' ? (
        engines.length === 0 ? (
          <div className="card">
            <EmptyState icon="⚙️" message={t('profiles.emptyEngineProfiles')} />
          </div>
        ) : (
          <div className="card overflow-hidden animate-fade-in">
            {/* Desktop table */}
            <table className="w-full border-collapse hidden sm:table">
              <thead>
                <tr className="divider">
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('profiles.brand')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('profiles.type')}
                  </th>
                  <th className="w-[80px] text-right px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted" />
                </tr>
              </thead>
              <tbody>
                {engines.map((en, idx) => (
                  <tr
                    key={en.id}
                    className={`hover:bg-bg-hover transition-colors ${idx < engines.length - 1 ? 'divider' : ''}`}
                  >
                    <td className="px-4 py-3 text-sm text-text-primary font-medium">{en.brand}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary">{en.type}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => { setEditEngine(en); setIsEditingEngine(true); }}
                          className="p-1.5 text-text-muted hover:text-text-primary rounded-md cursor-pointer transition-colors"
                          title={t('common.edit')}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteEngineTarget(en)}
                          className="p-1.5 text-text-muted hover:text-red rounded-md cursor-pointer transition-colors"
                          title={t('common.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile list */}
            <div className="sm:hidden">
              {engines.map((en, idx) => (
                <div
                  key={en.id}
                  className={`p-4 hover:bg-bg-hover transition-colors ${idx < engines.length - 1 ? 'divider' : ''}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary">{en.brand}</div>
                      <div className="text-xs text-text-secondary mt-0.5">{en.type}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => { setEditEngine(en); setIsEditingEngine(true); }}
                        className="p-1.5 text-text-muted hover:text-text-primary rounded-md cursor-pointer transition-colors"
                        title={t('common.edit')}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteEngineTarget(en)}
                        className="p-1.5 text-text-muted hover:text-red rounded-md cursor-pointer transition-colors"
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ) : (
        tyres.length === 0 ? (
          <div className="card">
            <EmptyState icon="🏎️" message={t('profiles.emptyTyreProfiles')} />
          </div>
        ) : (
          <div className="card overflow-hidden animate-fade-in">
            {/* Desktop table */}
            <table className="w-full border-collapse hidden sm:table">
              <thead>
                <tr className="divider">
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('profiles.brand')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('profiles.type')}
                  </th>
                  <th className="w-[120px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('profiles.compound')}
                  </th>
                  <th className="w-[80px] text-right px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted" />
                </tr>
              </thead>
              <tbody>
                {tyres.map((ty, idx) => (
                  <tr
                    key={ty.id}
                    className={`hover:bg-bg-hover transition-colors ${idx < tyres.length - 1 ? 'divider' : ''}`}
                  >
                    <td className="px-4 py-3 text-sm text-text-primary font-medium">{ty.brand}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary">{ty.type}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ty.compound === 'dry' ? 'bg-accent/10 text-accent' : 'bg-accent-subtle text-accent-hover border border-accent/20'}`}>
                        {ty.compound === 'dry' ? t('profiles.compoundDry') : t('profiles.compoundWet')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => { setEditTyre(ty); setIsEditingTyre(true); }}
                          className="p-1.5 text-text-muted hover:text-text-primary rounded-md cursor-pointer transition-colors"
                          title={t('common.edit')}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteTyreTarget(ty)}
                          className="p-1.5 text-text-muted hover:text-red rounded-md cursor-pointer transition-colors"
                          title={t('common.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile list */}
            <div className="sm:hidden">
              {tyres.map((ty, idx) => (
                <div
                  key={ty.id}
                  className={`p-4 hover:bg-bg-hover transition-colors ${idx < tyres.length - 1 ? 'divider' : ''}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-text-primary">{ty.brand}</span>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[0.65rem] font-medium ${ty.compound === 'dry' ? 'bg-accent/10 text-accent' : 'bg-accent-subtle text-accent-hover border border-accent/20'}`}>
                          {ty.compound === 'dry' ? t('profiles.compoundDry') : t('profiles.compoundWet')}
                        </span>
                      </div>
                      <div className="text-xs text-text-secondary mt-0.5">{ty.type}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => { setEditTyre(ty); setIsEditingTyre(true); }}
                        className="p-1.5 text-text-muted hover:text-text-primary rounded-md cursor-pointer transition-colors"
                        title={t('common.edit')}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteTyreTarget(ty)}
                        className="p-1.5 text-text-muted hover:text-red rounded-md cursor-pointer transition-colors"
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Engine Dialog */}
      <AnimatePresence>
        {isEditingEngine && (
          <dialog className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 w-full h-full animate-fade-in" open>
            <div className="fixed inset-0" onClick={() => setIsEditingEngine(false)} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="relative w-full max-w-md bg-bg-raised border border-border-subtle rounded-xl shadow-2xl p-6"
            >
              <h3 className="text-lg font-bold text-text-primary mb-6">
                {editEngine ? t('profiles.editEngineProfile') : t('profiles.addEngineProfile')}
              </h3>
              <form onSubmit={handleSaveEngine} className="flex flex-col gap-4">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">{t('profiles.brand')} *</label>
                  <input
                    name="brand"
                    defaultValue={editEngine?.brand || ''}
                    className="editor-input w-full"
                    placeholder="e.g. Rotax"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">{t('profiles.type')} *</label>
                  <input
                    name="type"
                    defaultValue={editEngine?.type || ''}
                    className="editor-input w-full"
                    placeholder="e.g. Max Senior EVO"
                  />
                </div>
                <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-border-subtle">
                  <button
                    type="button"
                    onClick={() => setIsEditingEngine(false)}
                    className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-accent text-white text-sm font-medium rounded-md hover:bg-accent-hover transition-colors cursor-pointer"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </form>
            </motion.div>
          </dialog>
        )}
      </AnimatePresence>

      {/* Tyre Dialog */}
      <AnimatePresence>
        {isEditingTyre && (
          <dialog className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 w-full h-full animate-fade-in" open>
            <div className="fixed inset-0" onClick={() => setIsEditingTyre(false)} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="relative w-full max-w-md bg-bg-raised border border-border-subtle rounded-xl shadow-2xl p-6"
            >
              <h3 className="text-lg font-bold text-text-primary mb-6">
                {editTyre ? t('profiles.editTyreProfile') : t('profiles.addTyreProfile')}
              </h3>
              <form onSubmit={handleSaveTyre} className="flex flex-col gap-4">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">{t('profiles.brand')} *</label>
                  <input
                    name="brand"
                    defaultValue={editTyre?.brand || ''}
                    className="editor-input w-full"
                    placeholder="e.g. Mojo"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">{t('profiles.type')} *</label>
                  <input
                    name="type"
                    defaultValue={editTyre?.type || ''}
                    className="editor-input w-full"
                    placeholder="e.g. D5"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">{t('profiles.compound')} *</label>
                  <select name="compound" defaultValue={editTyre?.compound || 'dry'} className="editor-select w-full">
                    <option value="dry">{t('profiles.compoundDry')}</option>
                    <option value="wet">{t('profiles.compoundWet')}</option>
                  </select>
                </div>
                <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-border-subtle">
                  <button
                    type="button"
                    onClick={() => setIsEditingTyre(false)}
                    className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-accent text-white text-sm font-medium rounded-md hover:bg-accent-hover transition-colors cursor-pointer"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </form>
            </motion.div>
          </dialog>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={!!deleteEngineTarget}
        title={t('common.delete') + ' ' + t('entity.engineProfile')}
        message={t('crud.confirmDelete', { entity: t('entity.engineProfile').toLowerCase(), name: `${deleteEngineTarget?.brand} ${deleteEngineTarget?.type}` })}
        confirmLabel={t('common.delete')}
        onConfirm={handleDeleteEngine}
        onCancel={() => setDeleteEngineTarget(null)}
        loading={deleting}
      />
      <ConfirmDialog
        open={!!deleteTyreTarget}
        title={t('common.delete') + ' ' + t('entity.tyreProfile')}
        message={t('crud.confirmDelete', { entity: t('entity.tyreProfile').toLowerCase(), name: `${deleteTyreTarget?.brand} ${deleteTyreTarget?.type}` })}
        confirmLabel={t('common.delete')}
        onConfirm={handleDeleteTyre}
        onCancel={() => setDeleteTyreTarget(null)}
        loading={deleting}
      />
    </div>
  );
}
